import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Location, PercentPipe, formatDate } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, ActivatedRouteSnapshot, CanDeactivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AppPath } from '@app/app-path';
import { OrderStatus } from '@app/core/enums/order-status';
import { CustomerContact } from '@app/core/models/customer-contact.model';
import { Customer } from '@app/core/models/customer.model';
import { OrderAdDetail } from '@app/core/models/order-ad-detail.model';
import { Order } from '@app/core/models/order.model';
import { UserProfile } from '@app/core/models/user-profile';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppLocaleService } from '@app/core/services/app-locale.service';
import {CreateOrUpdateOrderResponse, OrdersService} from '@app/core/services/orders.service';
import { CassCurrencyPipe } from '@app/shared/directives/cass-currency.pipe';
import { UserAlertService } from '@app/shared/services/user-alert.service';
import { CargoDialogService } from '@cargo/services/cargo-dialog.service';
import { OrderAdDetailStatus } from '@core/enums/order-ad-detail-status';
import { OrderHistoryRecordType } from '@core/enums/order-history-record-type';
import { AppConfig } from '@core/models/app-config';
import { CassError } from '@core/models/cass-error';
import { Message } from '@core/models/message.model';
import { OrderHistoryRecord } from '@core/models/order-history-record';
import { OrderUpdate } from '@core/models/order-update.model';
import { Logger } from '@core/services/logger.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderEditRestriction } from '@order/models/order-edit-restriction';
import { OrderLineNavigationState } from '@order/models/order-line-navigation-state';
import { OrderNavigationState } from '@order/models/order-navigation-state';
import { OrderOwner } from '@order/models/order-owner';
import { OrderServicesNavigationState } from '@order/models/order-services-navigation-state';
import { OrderHistoryDialogComponent } from '@order/order/order-history-dialog/order-history-dialog.component';
import { PublisherPriceSummaryDialogComponent } from '@order/order/publisher-price-summary-dialog/publisher-price-summary-dialog.component';
import { CalculatePriceRequestService } from '@order/services/calculate-price-request.service';
import { InfoPopupService } from '@order/services/info-popup.service';
import { OrderHelperService } from '@order/services/order-helper.service';
import { OrderReportHelperService } from '@order/services/order-report-helper.service';
import { OrderStateService } from '@order/services/order-state.service';
import { OrderUpdateRequestService } from '@order/services/order-update-request.service';
import { ProductPlacementSizeComboService } from '@order/services/product-placement-size-combo.service';
import { StaticDataCacheService } from '@order/services/static-data-cache.service';
import { StrictPackageGroupsService } from '@order/services/strict-package-groups.service';
import { OrderHistoryRecordViewModel } from '@order/viewModels/order-history-record.viewModel';
import { OrderLineDetailViewModel } from '@order/viewModels/order-line-detail.viewModel';
import { MenuItem } from '@shared/components/cass-button-dropdown/cass-button-dropdown.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { DialogAction } from '@shared/models/dialog-action';
import { DialogData } from '@shared/models/dialog-data';
import { OrderCreationNavigationState } from '@shared/models/order-creation-navigation-state';
import { DateTimeService } from '@shared/services/date-time.service';
import { StatusService } from '@shared/services/status.service';
import { OrderStatusViewModel } from '@shared/viewModels/order-status.viewModel';
import { BehaviorSubject, Observable, Subject, combineLatest, distinctUntilChanged, filter, firstValueFrom, map, of, take, tap } from 'rxjs';
import { SubSink } from 'subsink';
import { OrderMaterialPanelComponent } from './order-material-panel/order-material-panel.component';
import { ExportReportModal } from './report/modal/export.report.modal';
import {AuthService} from '@core/services/auth.service';
import {ExternalPlannerLoginResponse} from '@core/models/external-planner-login-response.model';
import {HttpStatusCode} from '@angular/common/http';

export const OrderDeactivationGuard: CanDeactivateFn<OrderComponent> = (component: OrderComponent, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot) => {
	// allow unconditional navigation to the /orderline && /orderservices
	if (nextState && nextState.url && (nextState.url.includes(AppPath.OrderLine) || nextState.url.includes(AppPath.OrderServices))) {
		return true;
	}
	return component.canDeactivate(nextState, currentRoute);
};

export enum ReportType {
	CampaignReportPDF,
	CampaignPlanXLS,
	ProductionPlanXLS
}

enum ColumnKey {
	Select = "select",
	Status = "status",
	ProductId = "productId",
	Publisher = "publisher",
	Size = "size",
	PlacementId = "placementId",
	IssueDate = "issueDate",
	PlacementRequest = "placementRequest",
	PriceGross = "priceGross",
	DiscountAmount = "discountAmount",
	NetPriceExComm = "netPriceExComm",
	CommissionAmount = "commissionAmount",
	PriceNet = "priceNet",
	ConfirmationNumber = "confirmationNumber",
	MaterialId = "materialId",
	MaterialDeadline = "materialDeadline",
	BookingDeadline = "bookingDeadline"
}

type TableColumnDef = {
	key: ColumnKey,
	heading: string,
	displayValue?: (data: OrderLineDetailViewModel) => string,
	sortableValue?: (data: OrderLineDetailViewModel) => number,
	className?: string
};

@Component({
	selector: 'app-order',
	templateUrl: './order.component.html',
	styleUrls: ['./order.component.css'],
	changeDetection: ChangeDetectionStrategy.Default,
	providers: [CargoDialogService, OrderStateService]
})
export class OrderComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly COMPONENT_NAME = "OrderComponent";

	private _routeStateOrder: Order | undefined = undefined;
	private _routeStateOrderRequiresCalc = false;
	private _routeStateOrderUpdated = false;

	externalPlanInfo: ExternalPlannerLoginResponse | undefined = undefined
	customer?: Customer;
	agency?: Customer;
	messageId?: string;
	userProfile?: UserProfile;
	agencyContact?: CustomerContact;
	form: FormGroup;
	campaignName: FormControl;
	materialCompany: FormControl;
	materialContactFirstName: FormControl;
	materialContactLastName: FormControl;
	materialContactEmail: FormControl;
	materialContactPhone: FormControl;
	changeCampaignName: boolean = false;
	changeMaterialCompany: boolean = false;
	changeMaterialContactFirstName: boolean = false;
	changeMaterialContactLastName: boolean = false;
	changeMaterialContactEmail: boolean = false;
	changeMaterialContactPhone: boolean = false;

	orderAdDetailsDataSource = new MatTableDataSource<OrderLineDetailViewModel>();

	@ViewChild('orderLinesTable', { read: MatSort, static: false })
	orderAdDetailsMatSort: MatSort | null = null;

	@ViewChild(OrderMaterialPanelComponent) orderMaterialPanelComponent: OrderMaterialPanelComponent | undefined;

	private _orderLinesTableSort: Sort = {
		active: ColumnKey.IssueDate,
		direction: "asc"
	};

	private _orderOwner: OrderOwner | undefined;
	private _triggeringNotificationMessage: Message | undefined;

	protected downloadButtonDropdownOptions: MenuItem[] = [
		{
			label: 'ORDER_BASKET.DOWNLOAD_REPORT.MENU_ITEMS.CAMPAIGN_REPORT_XLS',
			action: () => this.processReport(ReportType.CampaignReportPDF)
		},
		{
			label: 'ORDER_BASKET.DOWNLOAD_REPORT.MENU_ITEMS.CAMPAIGN_PLAN_XLS',
			action: () => this.processReport(ReportType.CampaignPlanXLS)
		},
		{
			label: 'ORDER_BASKET.DOWNLOAD_REPORT.MENU_ITEMS.PRODUCTION_PLAN_XLS',
			action: () => this.processReport(ReportType.ProductionPlanXLS)
		},
	];

	constructor(
		public localeService: AppLocaleService,
		private _alertService: UserAlertService,
		private _appConfigService: AppConfigService,
		private _calculatePriceRequestService: CalculatePriceRequestService,
		private _cargoDialogService: CargoDialogService,
		private _dateTimeService: DateTimeService,
		private _dialog: MatDialog,
		private _formBuilder: FormBuilder,
		private _location: Location,
		private _logger: Logger,
		private _orderHelperService: OrderHelperService,
		private _orderService: OrdersService,
		private _orderStateService: OrderStateService,
		private _orderUpdateRequestService: OrderUpdateRequestService,
		private _overlay: Overlay,
		private _popoverService: InfoPopupService,
		private _productPlacementSizeComboService: ProductPlacementSizeComboService,
		private _route: ActivatedRoute,
		private _router: Router,
		private _snackBar: MatSnackBar,
		private _staticDataCacheService: StaticDataCacheService,
		private _statusService: StatusService,
		private _strictPackageGroupsService: StrictPackageGroupsService,
		private _translate: TranslateService,
		private _userProfilesService: UserProfilesService,
		private _changeDetector: ChangeDetectorRef,
		private _orderReportHelperService: OrderReportHelperService,
		private _authService: AuthService
	) {
		this._formBuilder = _formBuilder;
		this.currencyPipe = new CassCurrencyPipe(localeService, this._appConfigService);
		this.percentPipe = new PercentPipe(localeService.currentOrDefaultLocaleId);

		// TODO set max lengths
		this.campaignName = this._formBuilder.control("", { validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)] });
		this.materialCompany = this._formBuilder.control("");
		this.materialContactFirstName = this._formBuilder.control("");
		this.materialContactLastName = this._formBuilder.control("");
		this.materialContactEmail = this._formBuilder.control("", { validators: [Validators.email] });
		this.materialContactPhone = this._formBuilder.control("", {validators: [Validators.maxLength(16)]});

		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			if (nav.extras.state instanceof OrderNavigationState || nav.extras.state["className"] === "OrderNavigationState") {
				const state = nav.extras.state as OrderNavigationState;
				this._routeStateOrder = state.order;
				this._routeStateOrderRequiresCalc = state.needsCalc || false;
				this._routeStateOrderUpdated = state.isUpdated || false;

				this.hasUnsavedChanges = this._routeStateOrderUpdated;
				this._orderStateService.hasUnsavedChanges = this._routeStateOrderUpdated;

				this._orderOwner = state.orderOwner;

				if (state.orderLinesTableSort) {
					// to sort order lines table, use the sort data sent to and received from the source component; this way we keep the user selected sorting persistent
					this._orderLinesTableSort = state.orderLinesTableSort;
				}
			}
			else if (nav.extras.state instanceof OrderCreationNavigationState || nav.extras.state["className"] === "OrderCreationNavigationState") {
				const state = nav.extras.state as OrderCreationNavigationState;
				this._orderOwner = state?.orderOwner;
			}
			else if (nav.extras.state["triggeringNotificationMessage"]) {
				this._triggeringNotificationMessage = nav.extras.state["triggeringNotificationMessage"] as Message;
			}
			else {
				this._logger.error(this.COMPONENT_NAME, "Bug: the navigation state is not handled");
			}
		}
		this.form = new FormGroup({});
		this.agency = undefined;
	}

	// PRISA prototype hacks - should use proper VM for this component
	order: Order = new Order();

	orderStatusVM = new OrderStatusViewModel();

	orderLinesTableColumnDefs: TableColumnDef[] = [
		{
			key: ColumnKey.Status,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.STATUS",
			displayValue: (data: OrderLineDetailViewModel): string => this.getOrderLineStatus(data.orderLineModel)
		},
		{
			key: ColumnKey.ProductId,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PRODUCT",
			displayValue: (data: OrderLineDetailViewModel): string => this._staticDataCacheService.getProductById(data.orderLineModel.productId)?.label ?? ''
		},
		{
			key: ColumnKey.Publisher,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PUBLISHER",
			displayValue: (data: OrderLineDetailViewModel): string => this._staticDataCacheService.getProductById(data.orderLineModel.productId)?.company?.label ?? ''
		},
		{
			key: ColumnKey.Size,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.SIZE",
			displayValue: (data: OrderLineDetailViewModel): string => this.getSizeColumnData(data.orderLineModel),
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.depth * data.orderLineModel.width
		},
		{
			key: ColumnKey.PlacementId,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PLACEMENT",
			displayValue: (data: OrderLineDetailViewModel): string => this._staticDataCacheService.getPlacement(data.orderLineModel.packageId, data.orderLineModel.placementId)?.label ?? ''
		},
		{
			key: ColumnKey.IssueDate,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.ISSUE_DATE",
			displayValue: (data: OrderLineDetailViewModel): string => formatDate(data.orderLineModel.issueDate, this.localeService.dateFormat, this.localeService.currentOrDefaultLocaleId),
			sortableValue: (data: OrderLineDetailViewModel): number => (new Date(data.orderLineModel.issueDate)).valueOf()
		},
		{
			key: ColumnKey.BookingDeadline,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.BOOKING_DEADLINE",
			displayValue: (data: OrderLineDetailViewModel): string => data.orderLineModel.bookingDeadline ? formatDate(data.orderLineModel.bookingDeadline, this.localeService.timestampFormat, this.localeService.currentOrDefaultLocaleId) : '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.bookingDeadline ? (new Date(data.orderLineModel.bookingDeadline)).valueOf() : Number.POSITIVE_INFINITY
		},
		{
			key: ColumnKey.MaterialDeadline,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.MATERIAL_DEADLINE",
			displayValue: (data: OrderLineDetailViewModel): string => data.orderLineModel.materialDeadline ? formatDate(data.orderLineModel.materialDeadline, this.localeService.timestampFormat, this.localeService.currentOrDefaultLocaleId) : '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.materialDeadline ? (new Date(data.orderLineModel.materialDeadline)).valueOf() : Number.POSITIVE_INFINITY
		},
		{
			key: ColumnKey.PlacementRequest,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.REQUEST",
			displayValue: (data: OrderLineDetailViewModel): string => data.orderLineModel.placementRequest ?? ''
		},
		{
			key: ColumnKey.PriceGross,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.GROSS_PRICE",
			displayValue: (data: OrderLineDetailViewModel): string => this.currencyPipe.transform(data.orderLineModel.priceGross, this.order.currencyCode) ?? '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.priceGross,
			className: "align-right"
		},
		{
			key: ColumnKey.DiscountAmount,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.DISCOUNTS",
			displayValue: (data: OrderLineDetailViewModel): string => this.currencyPipe.transform(data.orderLineModel.discountAmount, this.order.currencyCode) ?? '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.discountAmount,
			className: "align-right"
		},
		{
			key: ColumnKey.NetPriceExComm,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.NET_EX_COMMISSION",
			displayValue: (data: OrderLineDetailViewModel): string => this.currencyPipe.transform(data.orderLineModel.priceGross - data.orderLineModel.discountAmount, this.order.currencyCode) ?? '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.priceGross - data.orderLineModel.discountAmount,
			className: "align-right"
		},
		{
			key: ColumnKey.CommissionAmount,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.COMMISSION",
			displayValue: (data: OrderLineDetailViewModel): string => this.currencyPipe.transform(data.orderLineModel.commissionAmount, this.order.currencyCode) ?? '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.commissionAmount,
			className: "align-right"
		},
		{
			key: ColumnKey.PriceNet,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.NET_PRICE",
			displayValue: (data: OrderLineDetailViewModel): string => this.currencyPipe.transform(data.orderLineModel.priceGross - data.orderLineModel.discountAmount - data.orderLineModel.commissionAmount, this.order.currencyCode) ?? '',
			sortableValue: (data: OrderLineDetailViewModel): number => data.orderLineModel.priceGross - data.orderLineModel.discountAmount - data.orderLineModel.commissionAmount,
			className: "align-right"
		},
		{
			key: ColumnKey.ConfirmationNumber,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.CONFIRMATION_NUM",
			displayValue: (data: OrderLineDetailViewModel): string => data.orderLineModel.confirmationNumber ?? ''
		},
		{
			key: ColumnKey.MaterialId,
			heading: "ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.MATERIAL",
			displayValue: (data: OrderLineDetailViewModel): string => this._orderHelperService.composeFullMaterialId(data.orderLineModel.orderId, data.orderLineModel.materialId)
		}
	];

	private readonly initialOrderLinesTableDisplayedColumns: ColumnKey[] = [ColumnKey.Select, ...this.orderLinesTableColumnDefs.map(x => x.key)];

	orderLinesTableDisplayedColumns: ColumnKey[] = [...this.initialOrderLinesTableDisplayedColumns];

	ColumnKey = ColumnKey; // make ColumnKey type available in HTML

	currencyPipe: CassCurrencyPipe;
	percentPipe: PercentPipe;

	restriction: OrderEditRestriction = OrderEditRestriction.createDefault();

	orderHasAds = false;
	isDirectOrder: boolean = true;
	private isUsingMaterialId: boolean = true;

	//canOrderBeSavedAsDraft = false;
	canOrderBeConfirmed = false;
	canOrderBeReserved = false;
	canOrderBeCancelled = false;

	canRemoveOrderLine = false;
	canModifyOrderLine = false;
	canChangeMaterial = false;

	isAutoExportToExternalPlannerEnabled: boolean | undefined = undefined;

	canAddOrderService = false;

	initialSelection: OrderLineDetailViewModel[] = [];
	selection = new SelectionModel<OrderLineDetailViewModel>(true, this.initialSelection);

	orderHistoryRecordsVMs = new Map<string, OrderHistoryRecordViewModel[]>();

	hasUnsavedChanges = false;
	private _enableChatToPublishers: string[] | undefined = undefined; // list of publishers chat is allowed with
	public isChatEnabled = false; // indicates that chat enabled on the order page, depending on the order and configuration

	private readonly _callingOut$ = new BehaviorSubject<boolean>(false);
	private _fullscreenProgressOverlayRef?: OverlayRef;
	private _appConfig: AppConfig | undefined;

	isConfirmationDeadlinePassed: boolean = false;
	isConfirmationDeadlineApproaching: boolean = false;

	private subs = new SubSink();

	ngOnInit(): void {

		this.externalPlanInfo = this._authService.getExternalPlannerInfo;

		this.form.addControl("campaignName", this.campaignName);
		this.form.addControl("materialCompany", this.materialCompany);
		this.form.addControl("materialContactFirstName", this.materialContactFirstName);
		this.form.addControl("materialContactLastName", this.materialContactLastName);
		this.form.addControl("materialContactPhone", this.materialContactPhone);
		this.form.addControl("materialContactEmail", this.materialContactEmail);

		this._fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this.subs.add(
			this._callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					const componentRef = this._fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
					if (componentRef) {
						this._translate.get("APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE").subscribe((msg) => componentRef.instance.displayString = msg);
					}
				}
				else {
					this._fullscreenProgressOverlayRef?.detach();
				}
			}));

		this.subs.add(
			this.selection.changed.subscribe((change: SelectionChange<OrderLineDetailViewModel>) => {
				const selectedOrderLinesCount = change.source.selected.length;
				//this.canChangeMaterial = selectedOrderLinesCount > 0 && this.restriction.canChangeMaterialNumberPerOrderLine;
				//this.canModifyOrderLine = selectedOrderLinesCount === 1 && this.restriction.canChangeProduct && this.restriction.canChangePlacement && this.restriction.canChangeSize && this.restriction.canChangeIssueDate;
				//this.canRemoveOrderLine = selectedOrderLinesCount > 0 && this.restriction.canRemoveOrderLine;

				// new design: the actions now depend on the restrictions of the selected order lines
				const orderLinesGroupRestreiction = this._orderHelperService.getOrderLinesEditRestriction(change.source.selected.map((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel));

				this.canRemoveOrderLine = selectedOrderLinesCount > 0 && orderLinesGroupRestreiction.canRemoveOrderLine;
				this.canChangeMaterial = !this._statusService.doesOrderHaveStatus(this.order, OrderStatus.Cancelled) && selectedOrderLinesCount > 0 && orderLinesGroupRestreiction.canChangeMaterialNumberPerOrderLine;
				this.canModifyOrderLine = !this._statusService.doesOrderHaveStatus(this.order, OrderStatus.Cancelled) && selectedOrderLinesCount === 1 && orderLinesGroupRestreiction.canChangeProduct && orderLinesGroupRestreiction.canChangePlacement && orderLinesGroupRestreiction.canChangeSize && orderLinesGroupRestreiction.canChangeIssueDate;
			}));

		// Init the static data cache service; read the order from the route state or get it by id if the orderId is provided via the query param; otherwise create new order
		const staticDataInit$ = this._staticDataCacheService.init();
		const productPlacementSizeComboInit$ = this._productPlacementSizeComboService.init();
		let order$: Observable<Order> | undefined = undefined;
		let orderHistory$: Observable<OrderHistoryRecord[]> = of([]);

		if (this._routeStateOrder) {
			if (this._routeStateOrderRequiresCalc) {
				// Calc is required for the order provided via the route state; curently not in use: the Order Line calculates the order before passing it here
				const calcRequest = this._calculatePriceRequestService.createCalculateDisplayOrderRequest(this._routeStateOrder);
				const routeStateOrder = this._routeStateOrder; // let's keep the order reference in the closure

				order$ = this._orderService.calculatePrice(calcRequest).pipe(
					map((calculatedOrder: Order) => {
						this._orderHelperService.updateOrderCostingWithCalcResult(routeStateOrder, calculatedOrder);
						this._orderHelperService.updateOrderLinesWithCalcResult(routeStateOrder.orderAdDetails, calculatedOrder.orderAdDetails);

						// don't update the material
						// this._orderHelperService.updateMaterial(routeStateOrder.orderAdDetails);

						routeStateOrder.orderServices = [...calculatedOrder.orderServices];
						return routeStateOrder;
					})
				);
			}
			else {
				// Calc is not required, use the Order instance passed via the state params of the route by the Order Line Component

				// don't update the material
				// this._orderHelperService.updateMaterial(this._routeStateOrder.orderAdDetails);

				order$ = of(this._routeStateOrder);
			}
		}
		else {
			// The order is not passed to the component via the navigation state, so look for the orderId in the query params
			const orderId = this._route?.snapshot?.queryParamMap?.get("orderId");
			if (orderId) {
				// Retrieve the order from the BE
				order$ = this._orderService.getOrderById(orderId).pipe(
					tap(order => {
						if (order && order.orderHeader && order.orderHeader.advertiserId) {
							this._userProfilesService.notifyCustomerAccessed(order.orderHeader.advertiserId);
						}
					})
				);

				// The messageId is used only if the orderId is provided in the query params. It is passed to OrderConversationsComponent to show/activate this message on the order page.
				this.messageId = this._route.snapshot.queryParamMap.get("messageId") || undefined;

				if (this._triggeringNotificationMessage && this._triggeringNotificationMessage.subType && this._triggeringNotificationMessage.subType.toUpperCase() === "MODIFIED") {
					// The order page was open using a Notification Message of Modified subtype, so order history is requested from the BE and will be displayed in the Order Lines table
					orderHistory$ = this._orderService.getOrderHistoryById(orderId);
				}
			}
			else {
				// Create new Order instance
				const newOrder = this.createNewOrder(this._orderOwner);
				order$ = of(newOrder);
			}
		}

		this._callingOut$.next(true);

		const userProfile$ = this._userProfilesService.userProfile$.pipe(
			filter((userProfile: UserProfile | undefined) => Boolean(userProfile)),
			// When this._userProfilesService.notifyCustomerAccessed() is invoked and causes the profile update, this prevents the 2nd run of the initialization code
			take(1));

		this.subs.add(
			// Use combineLatest to run Init and Calc as it does not cancel the observables when one of them fails. In this case the Calc may fail, but the Init will not be cancelled.
			combineLatest([this._appConfigService.config$, this._appConfigService.dbConfig$, userProfile$, staticDataInit$, productPlacementSizeComboInit$, order$, orderHistory$]).subscribe({
				next: ([appConfig, dbConfig, profile, _, __, order, orderHistory]) => {
					this._callingOut$.next(false);

					this._appConfig = appConfig;
					this._enableChatToPublishers = appConfig && appConfig.enableChatToPublishers;
					this.isAutoExportToExternalPlannerEnabled = appConfig && appConfig.autoExportToExternalPlanner;

					this.userProfile = profile;
					if (profile?.customers?.[0] != undefined) {
						// TODO this is not valid now that we support multiple customers
						this.agency = profile.customers[0];
					};

					if (order) {
						// workaround because API is not loading all fields correctly
						// this._orderHelperService.updateOrderHeaderPriceFieldsFromLines(order);
						this._orderHelperService.updateOrderStartEndDates(order);
						// end workaround
						this.setOrder(order, orderHistory);
					}
				},
				error: (error: CassError) => {
					this._callingOut$.next(false);
					this._alertService.errorByCassError(error);

					// In case of Calc error, let's keep the Order sent from the Order Line Component open
					if (this._routeStateOrder) {
						this.subs.add(
							// We can set the Order only when the static data init is complete, otherwise the code fails to render it.
							staticDataInit$.subscribe({
								next: () => {
									// this "if" is required only to supress compiler's warning
									if (this._routeStateOrder) {

										// don't update the material
										// this._orderHelperService.updateMaterial(this._routeStateOrder.orderAdDetails);

										this.setOrder(this._routeStateOrder);
									}
								}
							}));
					}
				}
			}));
	}

	ngAfterViewInit(): void {
		if (this.orderAdDetailsMatSort) {
			this.subs.add(
				this.orderAdDetailsMatSort.sortChange.subscribe((event: Sort) => {
					if (event.active) {
						this._orderLinesTableSort.active = event.active;
						this._orderLinesTableSort.direction = event.direction;
					}
				})
			);
		}
		else {
			this._logger.warning(this.COMPONENT_NAME, "Cannot access the sort object of the order lines table, sorting is disabled");
		}
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
		if (this._fullscreenProgressOverlayRef && this._fullscreenProgressOverlayRef.hasAttached()) {
			this._fullscreenProgressOverlayRef.detach();
		}

		this._cargoDialogService.cleanup();
	}

	get showCustomerAndAgency(): boolean {
		// Shown: profile has multiple customers, or the order has an other payer (agency)
		return !!this.order.orderHeader.otherPayerId
			|| (this._userProfilesService.userProfile$.value?.customers ?? []).length > 1;
	}

	get showExternalPlanId(): boolean {
		return !!(this._appConfig?.autoExportToExternalPlanner
			&& (this.order.orderHeader.externalPlanId || this.externalPlanInfo?.planId));
	}

	get getExternalPlanId(): string | undefined {
		return this.externalPlanInfo?.planId
	}

	get isOrderExported(): boolean {
		return !!(this.order.orderHeader.id && this.order.orderHeader.externalPlanId)
	}

	getOrderLineStatus(adDetail: OrderAdDetail): string {
		let translatedStatuses = [];
		if (!adDetail.id) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.NEW"));
		}
		else if (this.orderStatusVM.draft) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.DRAFT"));
		}
		else if (adDetail.statusFlags.includes(OrderAdDetailStatus.ApprovalPending)) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.APPROVAL_PENDING"));
		}
		else if (adDetail.statusFlags.includes(OrderAdDetailStatus.Confirmed)) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.CONFIRMED"));
		}
		else if (adDetail.statusFlags.includes(OrderAdDetailStatus.Rejected)) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.REJECTED"));
		}
		else if (adDetail.statusFlags.includes(OrderAdDetailStatus.Cancelled)) {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.CANCELLED"));
		}
		else {
			translatedStatuses.push(this._translate.instant("ORDER_BASKET.STATUS.RESERVED"));
		}
		return translatedStatuses.join("\n");
	}

	updateField(fieldName: string, clearFlag: boolean) {
		setTimeout(() => {
			this.updateFieldImpl(fieldName, clearFlag);
			this._changeDetector.detectChanges();
		}, 0);
	}

	updateFieldImpl(fieldName: string, clearFlag: boolean) {
		//TODO Refactor but it works. 🙈🙉🙊
		switch (fieldName) {
			case "campaignName":
				if (this.order.orderHeader.campaignName == null) {
					this.order.orderHeader.campaignName = "";
				}
				if (this.changeCampaignName && (!(this.order.orderHeader.campaignName === this.getFormValueIfNotEmpty("campaignName")))) {
					this.order.orderHeader.campaignName = this.getFormValueIfNotEmpty("campaignName");
					this.changeCampaignName = false;
				} else if (clearFlag) {
					this.changeCampaignName = !clearFlag;
				} else {
					this.changeCampaignName = !this.changeCampaignName;
					this.changeCampaignName = !clearFlag;
				}
				break;
			case "materialCompany":
				if (this.order.materialAgencyName == null) {
					this.order.materialAgencyName = "";
				}
				if (this.changeMaterialCompany && (!(this.order.materialAgencyName === this.getFormValueIfNotEmpty("materialCompany")))) {
					this.order.materialAgencyName = this.getFormValueIfNotEmpty("materialCompany");
					this.changeMaterialCompany = false;
				} else if (clearFlag) {
					this.changeMaterialCompany = !clearFlag;
				} else {
					this.changeMaterialCompany = !this.changeMaterialCompany;
					this.changeMaterialCompany = !clearFlag;
				}
				break;
			case "materialContactFirstName":
				if (this.order.materialContactFirstName == null) {
					this.order.materialContactFirstName = "";
				}
				if (this.changeMaterialContactFirstName && (!(this.order.materialContactFirstName === this.getFormValueIfNotEmpty("materialContactFirstName")))) {
					this.order.materialContactFirstName = this.getFormValueIfNotEmpty("materialContactFirstName");
					this.changeMaterialContactFirstName = false;
				} else if (clearFlag) {
					this.changeMaterialContactFirstName = !clearFlag;
				} else {
					this.changeMaterialContactFirstName = !this.changeMaterialContactFirstName;
					this.changeMaterialContactFirstName = !clearFlag;
				}
				break;
			case "materialContactLastName":
				if (this.order.materialContactLastName == null) {
					this.order.materialContactLastName = "";
				}
				if (this.changeMaterialContactLastName && (!(this.order.materialContactLastName === this.getFormValueIfNotEmpty("materialContactLastName")))) {
					this.order.materialContactLastName = this.getFormValueIfNotEmpty("materialContactLastName");
					this.changeMaterialContactLastName = false;
				} else if (clearFlag) {
					this.changeMaterialContactLastName = !clearFlag;
				} else {
					this.changeMaterialContactLastName = !this.changeMaterialContactLastName;
					this.changeMaterialContactLastName = !clearFlag;
				}
				break;
			case "materialContactEmail":
				if (this.order.materialContactEmail == null) {
					this.order.materialContactEmail = "";
				}
				if (this.changeMaterialContactEmail && (!(this.order.materialContactEmail === this.getFormValueIfNotEmpty("materialContactEmail")))) {
					this.order.materialContactEmail = this.getFormValueIfNotEmpty("materialContactEmail");
					this.changeMaterialContactEmail = false;
				} else if (clearFlag) {
					this.changeMaterialContactEmail = !clearFlag;
				} else {
					this.changeMaterialContactEmail = !this.changeMaterialContactEmail;
					this.changeMaterialContactEmail = !clearFlag;
				}
				break;
			case "materialContactPhone":
				if (this.order.materialContactPhone == null) {
					this.order.materialContactPhone = "";
				}
				if (this.changeMaterialContactPhone && (!(this.order.materialContactPhone === this.getFormValueIfNotEmpty("materialContactPhone")))) {
					this.order.materialContactPhone = this.getFormValueIfNotEmpty("materialContactPhone");
					this.changeMaterialContactPhone = false;
				} else if (clearFlag) {
					this.changeMaterialContactPhone = !clearFlag;
				} else {
					this.changeMaterialContactPhone = !this.changeMaterialContactPhone;
					this.changeMaterialContactPhone = !clearFlag;
				}
				break;
			default:
				this.updateField("campaignName", true);
				this.updateField("materialCompany", true);
				this.updateField("materialContactFirstName", true);
				this.updateField("materialContactLastName", true);
				this.updateField("materialContactPhone", true);
				this.updateField("materialContactEmail", true);
		}

		this.hasUnsavedChanges = true;
		this._orderStateService.hasUnsavedChanges = true;
		return;
	}

	loadFormValues(order: Order) {
		this.campaignName.setValue(order.orderHeader.campaignName);
		this.materialCompany.setValue(order.materialAgencyName);
		this.materialContactFirstName.setValue(order.materialContactFirstName);
		this.materialContactLastName.setValue(order.materialContactLastName);
		this.materialContactPhone.setValue(order.materialContactPhone);
		this.materialContactEmail.setValue(order.materialContactEmail);
	}

	// TODO redundant logic
	getFormValueIfNotEmpty(fieldName: string) {
		return this.form?.controls[fieldName]?.value;
	}

	orderLineHasHistory(index: number, row: OrderLineDetailViewModel) {
		return this.orderHistoryRecordsVMs.has(row.orderLineModel.id);
	}

	private getSizeColumnData(data: OrderAdDetail): string {
		const size = this._productPlacementSizeComboService.getFixedSizeByDimensions(data.packageId, data.placementId, data.moduleCode, data.columns, data.depth);
		if (size) {
			return `${size.label}\n${data.width}x${data.depth}`
		}
		else {
			return `${data.width}x${data.depth}`;
		}
	}

	private setOrder(order: Order, orderHistory: OrderHistoryRecord[] | undefined = undefined): void {

		this.selection.clear();

		this.orderHasAds = false;
		//this.canOrderBeSavedAsDraft = false;
		this.canOrderBeReserved = false;

		if (!order) {
			return;
		}

		this.order = order;

		this.orderHasAds = order.orderAdDetails.length > 0;
		this.orderStatusVM = new OrderStatusViewModel(order.orderHeader, order.orderAdDetails);

		//this.canOrderBeSavedAsDraft = this.orderStatusVM.new || this.orderStatusVM.draft;
		//this.canOrderBeConfirmed = !this.orderStatusVM.cancelled;
		//this.canOrderBeReserved = !this.orderStatusVM.cancelled && !this.orderStatusVM.confirmedAll;

		this.canOrderBeConfirmed = this._statusService.canConfirmOrder(this.order);
		this.canOrderBeReserved = this._statusService.canReserveOrder(this.order);

		this.restriction = this._orderHelperService.getOrderEditRestriction(order);

		this.canOrderBeCancelled = Boolean(!this._statusService.doesOrderHaveStatus(this.order, OrderStatus.Cancelled) && this._appConfig && this._appConfig.orderCancellationEnabled && (this.orderStatusVM.draft || !this._orderHelperService.getSomeOrderLinesPastBookingDate(order.orderAdDetails)));

		this.canAddOrderService = !this._statusService.doesOrderHaveStatus(this.order, OrderStatus.Cancelled);

		this.agencyContact = this._userProfilesService.getAgencyContact(order.orderHeader.otherPayerId, order.orderHeader.agencyContactId);

		// If agencyContact is undefined re-fetch the user profile to get updated customer & contact details
		if (!this.agencyContact) {
			this._userProfilesService.getUserProfile(true).subscribe({
				next: (userProfile: UserProfile | undefined) => {
					if (userProfile) {
						this.agencyContact = this._userProfilesService.getAgencyContact(order.orderHeader.otherPayerId, order.orderHeader.agencyContactId);
					}
				}
			})
		}

		this.loadFormValues(order);

		this.isDirectOrder = this._orderHelperService.getIsDirectOrder(this.order);
		this.isUsingMaterialId = this._orderHelperService.areMaterilalIdsInUse(this.order.orderAdDetails);
		this.orderLinesTableDisplayedColumns = this.getOrderLinesTableDisplayedColumnKeys(this.initialOrderLinesTableDisplayedColumns, this.isDirectOrder, this.isUsingMaterialId);

		this.updateOrderAdDetailsDataSource(order.orderAdDetails);
		this.orderHistoryRecordsVMs = this.createHistoryViewModels(this._triggeringNotificationMessage, orderHistory);

		this.isConfirmationDeadlinePassed = Object.entries(order.orderHeader.statusCounts || {}).some(x => x[0] === OrderStatus.PastConfirmDeadline && (x[1] as number) > 0);
		this.isConfirmationDeadlineApproaching = !this.isConfirmationDeadlinePassed && Object.entries(order.orderHeader.statusCounts || {}).some(x => x[0] === OrderStatus.ConfirmDeadlineApproaching && (x[1] as number) > 0);

		this.isChatEnabled = Boolean(order.orderHeader && order.orderHeader.id && this._enableChatToPublishers && this._enableChatToPublishers.length > 0 && !this.orderStatusVM.draft);

		this.orderMaterialPanelComponent?.update();
	}

	/**
	 * Converts backend history records into view models, filtering them first by historyType, timestamp, ssUser, externalCompany, username.
	 * The records are filtered based on the information provided in the triggeringNotificationMessage
	 * @param triggeringNotificationMessage
	 * @param orderHistory
	 * @returns Map<string, OrderHistoryRecordViewModel[]> where the key is Order Line Id and the data is an array of
	 */
	private createHistoryViewModels(triggeringNotificationMessage: Message | undefined, orderHistory: OrderHistoryRecord[] | undefined): Map<string, OrderHistoryRecordViewModel[]> {
		const orderHistoryRecordsVMs = new Map<string, OrderHistoryRecordViewModel[]>();

		if (triggeringNotificationMessage && orderHistory) {
			const notificationTime = (new Date(triggeringNotificationMessage.timestamp)).getTime();

			const filteredRecords = orderHistory.filter((historyRecord: OrderHistoryRecord) => {
				// Spec, remove after the intergration is complete and everything works perfectly
				// The change history entries deemed "related" to a specific notification are those which fulfil the following criteria:
				// history.historyType = "MODIFIED"
				// history.timestamp is within 30 seconds of message.timestamp
				// history.ssUser = message.senderUserID(null should only match null)
				// history.externalCompany = message.senderCompanyId(null should only match null)
				// history.username = message.senderUserName(null should only match null) <-- this rule is obsolete, removed

				if (historyRecord.historyType !== OrderHistoryRecordType.Modified) {
					return false;
				}

				if (historyRecord.timestamp && Math.abs((new Date(historyRecord.timestamp)).getTime() - notificationTime) > 30e3) {
					// the time differnce is more than 30 sec
					return false;
				}

				if (this._triggeringNotificationMessage) { // this is required only to supress the code analyzer warning
					if (historyRecord.ssUser !== this._triggeringNotificationMessage.senderUserID) {
						return false;
					}

					if (historyRecord.externalCompany !== this._triggeringNotificationMessage.senderCompanyId) {
						return false;
					}
				}

				return true;
			});

			for (const filteredRecord of filteredRecords) {
				const historyVM = new OrderHistoryRecordViewModel(filteredRecord);

				let orderLineHistoryVMs: OrderHistoryRecordViewModel[] | undefined = orderHistoryRecordsVMs.get(historyVM.orderLineId);
				if (orderLineHistoryVMs) {
					orderLineHistoryVMs.push(historyVM);
				}
				else {
					orderLineHistoryVMs = [historyVM];
					orderHistoryRecordsVMs.set(historyVM.orderLineId, orderLineHistoryVMs);
				}
			}
		}

		return orderHistoryRecordsVMs;
	}

	private createNewOrder(orderOwner: OrderOwner | undefined): Order {
		const order = new Order();
		Object.assign(order.orderHeader, {
			adCount: 0,
			adCountRemaining: 0,
			bookedDate: formatDate(new Date(), "yyyy-MM-dd", this.localeService.currentOrDefaultLocaleId),
			priceCommissionRate: 0,
			//			priceCommissionTotal: 0,
			discountAmount: 0,
			priceGross: 0,
			priceNetExComm: 0,
			priceNetExTax: 0,
			priceNetIncTax: 0,
			statusFlags: []
		});

		order.orderAdDetails = [];

		if (orderOwner) {
			order.orderHeader.advertiserId = orderOwner.advertiserId;
			order.orderHeader.advertiserName = orderOwner.advertiserName;
			order.orderHeader.otherPayerId = orderOwner.otherPayerId;
			order.orderHeader.otherPayerName = orderOwner.otherPayerName;

			if (order.orderHeader.advertiserId) {
				this._userProfilesService.notifyCustomerAccessed(order.orderHeader.advertiserId);
			}
			else {
				this._logger.error(this.COMPONENT_NAME, "Bug: the createNewOrder() method was called with empty orderCreateParams.advertiserId parameter.");
			}
		}
		else {
			this._logger.error(this.COMPONENT_NAME, "Bug: the createNewOrder() method was called with empty orderCreateParams.");
		}

		this.orderStatusVM = new OrderStatusViewModel(order.orderHeader);

		return order;
	}

	// TODO: remove, replace with flags
	orderHasStatus(order: Order, status: string): boolean {
		return order.orderHeader?.statusFlags?.includes(status) ?? false;
	}

	addOrderLine() {
		const state = new OrderLineNavigationState();
		state.order = this.order;
		state.restriction = this.restriction;
		state.orderOwner = this._orderOwner;
		state.orderHasUnsavedChanges = this._orderStateService.hasUnsavedChanges || this._orderStateService.hasUnsavedMaterialChanges;
		state.orderLinesTableSort = this._orderLinesTableSort;

		this._router.navigate([AppPath.OrderLine], {
			state: state
		});
	}

	modifyOrderLine() {
		if (this.selection.hasValue()) {

			const modifyActionId = "modify";
			const cancelActionId = "cancel";

			const dialogData = new DialogData();
			dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.ARE_YOU_SURE_DIALOG_TITLE";
			dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.MODIFY_ORDER_LINE";
			dialogData.actions = [
				new DialogAction(cancelActionId, "ACTION_BUTTON_LABELS.CANCEL", true),
				new DialogAction(modifyActionId, "ACTION_BUTTON_LABELS.CONTINUE", false),
			];

			const dialogRef = this._dialog.open(DialogComponent, {
				data: dialogData
			});

			dialogRef.afterClosed().subscribe({
				next: (actionId: string) => {
					if (actionId === modifyActionId) {
						this.modifyOrderLineImpl();
					}
				},
				error: (error) => {
					this._logger.error(this.COMPONENT_NAME, `modifyOrderLine() dialog returned an error: "${error}"`);
				}
			});
		}
	}

	private modifyOrderLineImpl() {
		if (this.selection.hasValue()) {
			const selected = this.selection.selected;
			if (selected && selected.length > 0) {

				const state = new OrderLineNavigationState();
				state.order = this.order;
				state.orderAdDetail = selected[0].orderLineModel;
				state.restriction = this.restriction;
				state.orderHasUnsavedChanges = this.hasUnsavedChanges;
				this._orderStateService.hasUnsavedChanges = this.hasUnsavedChanges;
				state.orderLinesTableSort = this._orderLinesTableSort;

				this._router.navigate([AppPath.OrderLine], {
					state: state
				});
			}
		}
	}

	removeOrderLines(): void {
		const removeActionId = "remove";
		const cancelActionId = "cancel";

		const dialogData = new DialogData();
		dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.ARE_YOU_SURE_DIALOG_TITLE";
		dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.REMOVE_ORDER_LINES";
		dialogData.actions = [
			new DialogAction(cancelActionId, "ACTION_BUTTON_LABELS.CANCEL", true),
			new DialogAction(removeActionId, "ACTION_BUTTON_LABELS.REMOVE", false, "mat-warn")
		];

		if (this.selectionHasConfirmedOrderLines()) {
			dialogData.dialogType = "warning";
			dialogData.footerKey = "ORDER_BASKET.VALIDATION_MESSAGES.REMOVE_CONFIRMED_ORDER_LINES_WARNING";
		}

		const dialogRef = this._dialog.open(DialogComponent, {
			data: dialogData,
			restoreFocus: false
		});

		dialogRef.afterClosed().subscribe({
			next: (actionId: string) => {
				if (actionId === removeActionId) {
					this.removeOrderLinesImpl();
				}
			},
			error: (error) => {
				this._logger.error(this.COMPONENT_NAME, `removeOrderLines() dialog returned an error: "${error}"`);
			}
		});
	}

	private selectionHasConfirmedOrderLines(): boolean {

		const selectedOrderLines: OrderLineDetailViewModel[] = this.selection.selected;

		if (selectedOrderLines.some((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel.confirmationNumber)) {
			return true;
		}
		return false;
	}

	private removeOrderLinesImpl(): void {
		if (this.selection.selected.length > 0) {
			this.order.orderAdDetails = this.order.orderAdDetails.filter((orderLine: OrderAdDetail) => !this.selection.selected.some((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel === orderLine));
			this.selection.clear();
			this.hasUnsavedChanges = true;
			this._orderStateService.hasUnsavedChanges = true;

			if (this.order.orderAdDetails.length > 0) {
				const calcRequest = this._calculatePriceRequestService.createCalculateDisplayOrderRequest(this.order);

				this._callingOut$.next(true);
				this._orderHelperService.resetOrderCosting(this.order);
				this._orderService.calculatePrice(calcRequest).subscribe({
					next: (calculatedOrder: Order) => {
						this._callingOut$.next(false);
						this._orderHelperService.updateOrderCostingWithCalcResult(this.order, calculatedOrder);
						this._orderHelperService.updateOrderLinesWithCalcResult(this.order.orderAdDetails, calculatedOrder.orderAdDetails);
						//						this._orderHelperService.updateOrderHeaderPriceFieldsFromLines(this.order);
						this._orderHelperService.updateOrderStartEndDates(this.order);
						this.setOrder(this.order);
					},
					error: (error: CassError) => {
						this._callingOut$.next(false);
						this._logger.error(this.COMPONENT_NAME, "Calculate upon order lines removal failed", error);
						this._alertService.errorByCassError(error);
					}
				});
			}
			else {
				// no order lines left, just reset the costing
				this._orderHelperService.resetOrderCosting(this.order);
				this.setOrder(this.order);
			}
		}
	}


	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.order.orderAdDetails?.length;
		return numSelected == numRows;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	toggleAllRows() {
		this.isAllSelected() ?
			this.selection.clear() :
			//this.order.orderAdDetails?.forEach(row => this.selection.select(row));
			this.orderAdDetailsDataSource.data.forEach(row => this.selection.select(row));
	}

	onCellMouseEnter(row: OrderLineDetailViewModel, columnKey: string, event: MouseEvent): void {
		if (columnKey === "productId") {
			if (event && event.target && row && row.orderLineModel.packageId) {
				this._popoverService.notifyMouseEntersPackageLabel(row.orderLineModel.packageId, event.target);
			}
		}
	}

	onCellMouseLeave(row: OrderLineDetailViewModel, columnKey: string, event: MouseEvent): void {
		if (columnKey === "productId") {
			if (event && event.target && row && row.orderLineModel.packageId) {
				this._popoverService.notifyMouseLeavesPackageLabel(row.orderLineModel.packageId);
			}
		}
	}

	async saveDraftAsync(): Promise<void> {
		// CASS-96: Allow setting status of order to 'offer/quote' representing a tentative order for the purpose of pricing, inventory check and negotiation between agency and their client.
		// These are not sent to media system. The order page will have a "SAVE AS DRAFT" button which will set the requestStatus property to "offer" in the POST/orders/orders API call.
		// i.e. orderUpdate.requestStatus="offer"

		if (!this.preSaveValidation()) return;

		if (this.hasOrderLinesPastConfirmDealine(this.orderAdDetailsDataSource.data)) {
			await firstValueFrom(this.showNotificationDialog$("ORDER_BASKET.CONFIRMATION_DEADLINE_WARNING.CONFIRM_DEADLINE_PASSED_DIALOG_TITLE", "ORDER_BASKET.CONFIRMATION_DEADLINE_WARNING.CONFIRM_DEADLINE_PASSED_DIALOG_MSG"));
		}

		this.order.orderHeader.externalPlanId = this.order.orderHeader.externalPlanId || this.getExternalPlanId
		const orderUpdate = this._orderUpdateRequestService.createDisplayOrderUpdateRequest(this.order, { isOffer: true, isAutoExportToExternalPlanner: this.isAutoExportToExternalPlannerEnabled });
		this.saveOrder(orderUpdate);
	}

	async reserveAsync(): Promise<void> {
		// CASS-97: Allow configuring per product / placement whether approval by media house is required for self-service confirmation. Pending approval is represented by a stop code on the (preliminary/unconfirmed) order.
		// Media house must approve(release stop code) before confirmation is possible.
		// In the edit order page, show approval required in the status column for order lines with placements that have the approvalRequired property set and the order line is not yet approved.
		// If any order line requires approval, the order cannot be confirmed, but can be reserved.

		if (!this.preSaveValidation()) return;

		if (this.hasOrderLinesPastConfirmDealine(this.orderAdDetailsDataSource.data)) {
			await firstValueFrom(this.showNotificationDialog$("ORDER_BASKET.CONFIRMATION_DEADLINE_WARNING.CONFIRM_DEADLINE_PASSED_DIALOG_TITLE", "ORDER_BASKET.CONFIRMATION_DEADLINE_WARNING.CONFIRM_DEADLINE_PASSED_DIALOG_MSG"));
		}

		this.order.orderHeader.externalPlanId = this.order.orderHeader.externalPlanId || this.getExternalPlanId;
		const orderUpdate = this._orderUpdateRequestService.createDisplayOrderUpdateRequest(this.order, {isAutoExportToExternalPlanner: this.isAutoExportToExternalPlannerEnabled});
		this.saveOrder(orderUpdate);
	}

	confirm(): void {
		if (!this.preSaveValidation()) return;

		if (this.selection.selected.length === 0) {
			// notify user that no order lines are selected
			this.showNotificationDialog$("ORDER_BASKET.CONFIRM_ORDER.WRONG_SELECTION_NOTIFICATION_TITLE", "ORDER_BASKET.CONFIRM_ORDER.SELECT_ORDER_LINES_MSG");
			return;
		}

		let invalidSelectionMessageKey = "";
		const isSelectionInvalid = this.selection.selected.some((orderLineVM: OrderLineDetailViewModel) => {

			if (this._statusService.doesAdDetailHaveStatus(orderLineVM.orderLineModel, OrderAdDetailStatus.ApprovalPending)
			|| this._statusService.doesAdDetailHaveStatus(orderLineVM.orderLineModel, OrderAdDetailStatus.Rejected)) {
				// notify user that some of the selected order lines require approval first
				invalidSelectionMessageKey = "ORDER_BASKET.CONFIRM_ORDER.SOME_LINES_CANNOT_BE_CONFIRMED_MSG"
				return true;
			}
			if (this._statusService.doesAdDetailHaveStatus(orderLineVM.orderLineModel, OrderAdDetailStatus.Cancelled)) {
				// notify user that some of the selected order lines are canceled
				invalidSelectionMessageKey = "ORDER_BASKET.CONFIRM_ORDER.SOME_LINES_ARE_CANCELLED"
				return true;
			}
			return false;

			// TODO remove this condition for now. This will be altered later after further requirements analysis.
			// || this._statusService.doesAdDetailHaveStatus(orderLine, OrderAdDetailStatus.Confirmed);
		});

		if (isSelectionInvalid) {
			this.showNotificationDialog$("ORDER_BASKET.CONFIRM_ORDER.WRONG_SELECTION_NOTIFICATION_TITLE", invalidSelectionMessageKey);
			return;
		}

		const submitActionId = "submit";
		const cancelActionId = "cancel";

		const dialogData = new DialogData();
		dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.CONFIRM_ORDER_WITH_PUBLISHER_REQUEST";

		const detailsColl = this.selection.selected;

		if (this.isAutoExportToExternalPlannerEnabled && (this.getExternalPlanId || this.order.orderHeader.externalPlanId)) {
			dialogData.dialogType = "warning"
			dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.CONFIRMING_WILL_EXPORT_TO_MARATHON_MSG"
		} else {
			dialogData.prompt = 'ORDER_BASKET.VALIDATION_MESSAGES.CONFIRMATION_REQUEST';
		}


		dialogData.actions = [
			new DialogAction(submitActionId, "ACTION_BUTTON_LABELS.SUBMIT", true),
			new DialogAction(cancelActionId, "ACTION_BUTTON_LABELS.CANCEL", false),
		];

		const dialogRef = this._dialog.open(DialogComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().subscribe({
			next: (actionId: string) => {
				if (actionId === submitActionId) {
					if (detailsColl != null && detailsColl.length > 0) {
						let confirmationNumber = dialogRef?.componentInstance?.editField?.value;
						if (confirmationNumber != undefined && confirmationNumber != null && confirmationNumber.length > 0) {
							this._logger.debug(this.COMPONENT_NAME, "Order Confirmed. : with ConfirmationRequest : " + confirmationNumber);
							for (var cntr = 0; cntr < detailsColl.length; cntr++) {
								detailsColl[cntr].orderLineModel.confirmationNumber = confirmationNumber;
							}
						}
					}
					this.order.orderHeader.externalPlanId = this.order.orderHeader.externalPlanId || this.getExternalPlanId
					const orderUpdate = this._orderUpdateRequestService.createDisplayOrderUpdateRequest(this.order, { requestConfirmOrderAdDetails: detailsColl.map(x => x.orderLineModel), isAutoExportToExternalPlanner: this.isAutoExportToExternalPlannerEnabled });
					this.saveOrder(orderUpdate);
				}
			}
		});
	}

	private preSaveValidation(): boolean {
		// Strict package groups
		const msg = this._strictPackageGroupsService.hasInvalidStrictPackageGroups(this.order);
		if (msg) {
			this._alertService.messageByTranslateKey("ORDER_BASKET.VALIDATION_MESSAGES.STRICT_PACKAGE_GROUPS_NOT_VALID", { data: msg })
			return false;
		}

		// Mandatory fields in order header form
		this.form.updateValueAndValidity();
		if (!this.form.valid) {
			this.subs.add(this._alertService.messageByTranslateKey("ORDER_BASKET.VALIDATION_MESSAGES.PLEASE_FILL_IN_MANDATORY_FIELDS").subscribe(_ => {
				for (const ctl of Object.keys(this.form.controls)) {
					if (this.form.get(ctl)?.invalid) {
						// show field edit mode
						this.updateField(ctl, false);
					}
				}
			}));
			return false;
		}

		// All validation succeeded
		return true;
	}

	private hasOrderLinesPastConfirmDealine(orderLinesVMs: OrderLineDetailViewModel[]): boolean {
		return orderLinesVMs.some((orderLineVM: OrderLineDetailViewModel) => orderLineVM.isConfirmationDeadlinePassed);
	}

	discardChanges(): void {
		this.confirmDiscardChangesAsync().subscribe({
			next: (isConfirmed: boolean) => {
				if (isConfirmed) {
					this.discardChangesImpl();
				}
			}
		});
	}

	private discardChangesImpl() {
		// for new order, re-create bew order; for existing order, retrieve it from the BE
		if (this.orderStatusVM.isNew) {
			const newOrder = this.createNewOrder(this._orderOwner);
			this.setOrder(newOrder);
			this.hasUnsavedChanges = false;
			this._orderStateService.hasUnsavedChanges = false;
		}
		else {
			this._callingOut$.next(true);
			const orderId = this.order.orderHeader.id;
			if (orderId) {
				this._orderService.getOrderById(orderId).subscribe({
					next: (order) => {
						this._callingOut$.next(false);
						if (order) {
							// workaround because API is not loading all fields correctly
							// this._orderHelperService.updateOrderHeaderPriceFieldsFromLines(order);
							this._orderHelperService.updateOrderStartEndDates(order);
							// end workaround
							this.setOrder(order);
							this.hasUnsavedChanges = false;
							this._orderStateService.hasUnsavedChanges = false;
							this._orderStateService.hasUnsavedMaterialChanges = false;
						}
					},
					error: (error) => {
						this._callingOut$.next(false);
						this._logger.error(this.COMPONENT_NAME, `Cannot retrieve order "${orderId}" when the user discarded changes. Error: ${JSON.stringify(error)}`);
					}
				});
			}
		}
	}

	private saveOrder(orderUpdate: OrderUpdate): void {
		this._callingOut$.next(true);
		this._orderService.createOrUpdateOrder(orderUpdate).subscribe({
			next: (response: CreateOrUpdateOrderResponse) => {
				const updatedOrder = response.order;
				if (response.statusCode === HttpStatusCode.MultiStatus) {
					this._translate.get('ORDER_BASKET.EXTERNAL_PLANNER.MESSAGES.ORDER_NOT_SYNCHRONISED_TO_EXTERNAL_PLANNER')
						.subscribe(msg => {
							if (!msg) return
							this._alertService.showDialog(false, '', msg)
						})
				}
				this._callingOut$.next(false);
				this.hasUnsavedChanges = false;
				this._orderStateService.hasUnsavedChanges = false;
				this._orderStateService.hasUnsavedMaterialChanges = false;

				if (updatedOrder.orderHeader.externalPlanId) {
					this._authService.clearExternalPlannerInfo();
				}
				this.setOrder(updatedOrder);

				const orderSavedKey = orderUpdate.id ? "APP_NOTIFICATIONS.EXISTING_ORDER_SAVED" : "APP_NOTIFICATIONS.NEW_ORDER_SAVED";

				this._translate.get(orderSavedKey).subscribe((msg) => {
					const snackBarRef = this._snackBar.open(msg, undefined, {
						duration: 3000,
						verticalPosition: "top",
						horizontalPosition: "right",
						panelClass: "cass-notification-snackbar-success"
					});

					// Andrew: Group doesn't want to navigate back to dashboard (?)
					// snackBarRef.afterDismissed().subscribe(() => {
					// 	this._router.navigate(["dashboard"]);
					// });
				});

				// To properly handle the page refresh, the query params are updated with the saved order id, and the state is removed.
				// This way the saved order will be open for editing after page refresh.
				const url = this._router.createUrlTree([], { queryParams: { orderId: updatedOrder.orderHeader.id } }).toString();
				this._location.go(url);
			},
			error: (error: CassError) => {
				this._callingOut$.next(false);
				this._alertService.errorByCassError(error);
			}
		});
	}

	private showHideMaterialColumn(orderAdDetails: OrderAdDetail[]): void {
		this.isUsingMaterialId = this._orderHelperService.areMaterilalIdsInUse(orderAdDetails);
		this.orderLinesTableDisplayedColumns = this.getOrderLinesTableDisplayedColumnKeys(this.initialOrderLinesTableDisplayedColumns, this.isDirectOrder, this.isUsingMaterialId);
	}

	changeMaterial(): void {
		if (this._orderStateService.hasUnsavedChanges) {
			// materials can be changed only when all the changes are saved
			this._alertService.messageByTranslateKey("ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TEXT", undefined, "ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TITLE", undefined);
			return;
		}

		const selectedOrderLines: OrderAdDetail[] = this.selection.selected.map((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel);
		this._orderHelperService.changeMaterial(this.order.orderAdDetails, selectedOrderLines);

		this.showHideMaterialColumn(this.order.orderAdDetails);

		this._orderStateService.hasUnsavedMaterialChanges = true;
		this.hasUnsavedChanges = true;

		this.orderMaterialPanelComponent?.update();
	}

	// TODO: Remove, Replaced with processReport() method
	exportToExcel() {
		if (this.order.orderHeader.id) {
			this._dialog.open(ExportReportModal, { data: this.order.orderHeader.id });
		}
	}

	//private groupOrderAdDetailsBySize(orderAdDetails: OrderAdDetail[]): Map<string, OrderAdDetail[]> {
	//	// Map is used here instead of a Record because the Map keeps the order of the added items, so first order line will have material Id = "001"

	//	return orderAdDetails.reduce((acc, obj) => {
	//		const sizeName = obj.depth + "x" + obj.width;

	//		let arr = acc.get(sizeName);
	//		if (!arr) {
	//			arr = [];
	//			acc.set(sizeName, arr);
	//		}

	//		arr.push(obj);

	//		return acc;

	//	}, new Map<string, OrderAdDetail[]>);
	//}

	canDeactivate(nextState: RouterStateSnapshot, currentRoute: ActivatedRouteSnapshot): Observable<boolean> | boolean {

		const hasPlanId = !!this._authService.getExternalPlannerInfo?.planId
		const urlTree = this._router.parseUrl(nextState.url);

		if (!urlTree.queryParamMap.has('advertiserId') && !urlTree.queryParamMap.has('agencyId') && hasPlanId) {
			// External Plan ID should be discarded is user route to any other page except
			this._authService.clearExternalPlannerInfo()
		}
		if (!this._orderStateService.hasUnsavedChanges && !this._orderStateService.hasUnsavedMaterialChanges) {
			return true;
		}

		return this.confirmDiscardChangesAsync();
	}

	private confirmDiscardChangesAsync(): Observable<boolean> {
		const confirmActionId = "confirm";
		const cancelActionId = "cancel";

		const dialogData = new DialogData();
		dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.ARE_YOU_SURE_DIALOG_TITLE";
		dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.UNSAVED_CHANGES_MSG";

		dialogData.actions = [
			new DialogAction(cancelActionId, "ACTION_BUTTON_LABELS.CANCEL", false),
			new DialogAction(confirmActionId, "ACTION_BUTTON_LABELS.DISCARD_CHANGES", true)
		];

		const dialogRef = this._dialog.open(DialogComponent, {
			data: dialogData
		});

		return dialogRef.afterClosed().pipe(
			map((actionId: string) => actionId === confirmActionId)
		);
	}

	addService(): void {
		// create the map of packageId-to-packageLabel here, to avoid loading of static data in the order services component
		const uniquePackageIds = [...new Set(this.order.orderAdDetails.map((orderLine: OrderAdDetail) => orderLine.packageId))];
		const packageMap = new Map<string, string>();
		uniquePackageIds.forEach((packageId: string) => {
			const pckg = this._staticDataCacheService.getPackageById(packageId);
			if (pckg) {
				packageMap.set(packageId, pckg.label);
			}
		});

		const state = new OrderServicesNavigationState();
		state.order = this.order;

		// shortcut, may need to separate those in the future
		state.orderHasUnsavedChanges = this._orderStateService.hasUnsavedChanges || this._orderStateService.hasUnsavedMaterialChanges;

		state.packageMap = packageMap;

		this._router.navigate([AppPath.OrderServices], {
			state: state
		});
	}

	openPriceSummationModal() {
		this._dialog.open(PublisherPriceSummaryDialogComponent, {
			data: this.order,
			width: '80vw',
		});
	}

	private updateOrderAdDetailsDataSource(orderAdDetails: OrderAdDetail[]) {

		this.orderAdDetailsDataSource.data = [...orderAdDetails.map((orderLine: OrderAdDetail) => this.createOrderAdDetailViewModel(orderLine))];

		this.orderAdDetailsDataSource.sortingDataAccessor = (item: OrderLineDetailViewModel, sortHeaderId: string) => {
			const columnDef = this.orderLinesTableColumnDefs.find(x => x.key === sortHeaderId);
			if (columnDef) {
				return columnDef.sortableValue ? columnDef.sortableValue(item) : columnDef.displayValue ? columnDef.displayValue(item) : (item as any)[sortHeaderId] ?? '';
			}
		};

		setTimeout(() => {
			if (this.orderAdDetailsDataSource) {
				this.orderAdDetailsDataSource.sort = this.orderAdDetailsMatSort;
				if (this.orderAdDetailsDataSource.sort) {
					this.orderAdDetailsDataSource.sort.active = this._orderLinesTableSort.active;
					this.orderAdDetailsDataSource.sort.direction = this._orderLinesTableSort.direction;
					this.orderAdDetailsDataSource.sort.sortChange.emit({ active: this._orderLinesTableSort.active, direction: this._orderLinesTableSort.direction });
				}
			}
		}, 0);
	}

	private createOrderAdDetailViewModel(orderLine: OrderAdDetail): OrderLineDetailViewModel {
		const vm = new OrderLineDetailViewModel(orderLine);
		return vm;
	}

	private showNotificationDialog$(titleKey: string, messageKey: string): Observable<void> {
		const titleTranslation$ = this._translate.get(titleKey);
		const messageTranslation$ = this._translate.get(messageKey);

		// this mechanism allows to the caller to receive notification that the dialog is closed,
		// and to make the dialog shown even if the caller is not subscribed
		const dialogSubject = new Subject<void>();

		combineLatest([titleTranslation$, messageTranslation$]).subscribe(
			([title, msg]) => {
				this._alertService.showDialog(false, title, msg).subscribe(dialogSubject);
			});

		return dialogSubject.asObservable();
	}

	/**
	 * Provides an array of column keys hiding columns for direct orders and when material is is not in use
	 * NetPriceExComm and CommissionAmount are hidden for direct orders; MaterialId is hidden when isMaterialShown=false
	 * @param initialColumns array with all available column keys
	 * @param isDirectOrder
	 * @param isMaterialShown
	 * @returns array of column keys
	 */
	private getOrderLinesTableDisplayedColumnKeys(initialColumns: ColumnKey[], isDirectOrder: boolean, isMaterialShown: boolean): ColumnKey[] {
		return initialColumns.filter((key: ColumnKey) => {
			if (isDirectOrder && [ColumnKey.NetPriceExComm, ColumnKey.CommissionAmount].includes(key)) {
				return false;
			}

			if (!isMaterialShown && key === ColumnKey.MaterialId) {
				return false;
			}

			return true;
		});
	}

	cancel() {
		// 1.3. A warning confirmation dialog will shown before the action is executed. "The order will be cancelled. This cannot be undone.".

		const cancelOrderActionId = "cancelOrder";

		const dialogData = new DialogData();
		dialogData.dialogType = "warning";
		dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.ARE_YOU_SURE_DIALOG_TITLE";
		dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.ORDER_CANCELLATION_WARNING";
		dialogData.actions = [
			new DialogAction("keepOrder", "ORDER_BASKET.ACTION_BUTTON_LABELS.KEEP_ORDER", true),
			new DialogAction(cancelOrderActionId, "ORDER_BASKET.ACTION_BUTTON_LABELS.CANCEL_ORDER", false, "mat-warn")
		];

		const dialogRef = this._dialog.open(DialogComponent, {
			data: dialogData,
			restoreFocus: false
		});

		dialogRef.afterClosed().subscribe({
			next: (actionId: string) => {
				if (actionId === cancelOrderActionId) {
					this.cancelOrderImpl();
				}
			},
			error: (error) => {
				this._logger.error(this.COMPONENT_NAME, `Cancel order dialog returned an error: "${error}"`);
			}
		});
	}

	private cancelOrderImpl(): void {
		const request = this._orderHelperService.createCancelOrderRequest(this.order);
		this._callingOut$.next(true);
		this._orderService.cancelOrder(request).subscribe({
			next: (order) => {
				this._callingOut$.next(false);
				this.setOrder(order);
			},
			error: (error: CassError) => {
				this._callingOut$.next(false);
				this._alertService.errorByCassError(error);
			}
		});
	}

	viewHistory() {
		const orderHistoryDialog = this._dialog.open(OrderHistoryDialogComponent, {
			maxWidth: '50vw'
		});
		orderHistoryDialog.componentInstance.orderId = this.order.orderHeader.id
	}

	private processReport(reportType: ReportType) {
		// CASS-417 If there are no order lines, a message is shown: "Please add order lines and save your order before downloading reports." in a dialog.
		if (!this.orderHasAds) {
			this._alertService.messageByTranslateKey('ORDER_BASKET.DOWNLOAD_REPORT.VALIDATION_MESSAGES.HAS_NO_ORDER_LINES');
			this._logger.warning(this.COMPONENT_NAME, 'Please add order lines and save your order before downloading reports.');
			return;
		}

		// CASS-417 If there are order lines, and the order has unsaved changes, a message is shown: "Please save your order before downloading reports." in a dialog.
		if (this.hasUnsavedChanges) {
			this._alertService.messageByTranslateKey('ORDER_BASKET.DOWNLOAD_REPORT.VALIDATION_MESSAGES.HAS_UNSAVED_CHANGES');
			this._logger.warning(this.COMPONENT_NAME, 'Please save your order before downloading reports.');
			return;
		}

		const orderId = this.order.orderHeader.id
		let reportName: string = '';
		if (orderId) {
			switch (reportType) {
				case ReportType.CampaignPlanXLS:
					reportName = 'Campaign plan Report.xls';
					this.downloadCampaignAndProductionPlanReport(orderId, ReportType.CampaignPlanXLS, reportName);
					return;
				case ReportType.ProductionPlanXLS:
					reportName = 'Production Plan Report.xls';
					this.downloadCampaignAndProductionPlanReport(orderId, ReportType.ProductionPlanXLS, reportName);
					return;
				case ReportType.CampaignReportPDF:
					reportName = 'Campaign Report.pdf';
					this.downloadCampaignReportPDF(orderId, reportName);
					return;
				default:
					this._logger.warning(this.COMPONENT_NAME, 'Invalid Report Type');
					return;
			}
		} else {
			this._alertService.messageByTranslateKey('ORDER_BASKET.DOWNLOAD_REPORT.VALIDATION_MESSAGES.HAS_UNSAVED_CHANGES');
			this._logger.warning(this.COMPONENT_NAME, 'Please save your order before downloading reports.');
			return;
		}
	}

	downloadCampaignAndProductionPlanReport(orderId: string, reportType: ReportType, reportName: string) {
		this._callingOut$.next(true);
		this.subs.add(
			this._orderService.exportToXls(orderId, reportType)
				.subscribe({
					next: (fileBlob) => {
						this._orderReportHelperService.saveReportAsFile(fileBlob, reportName);
						this._callingOut$.next(false);
					},
					error: (error: CassError) => {
						this._callingOut$.next(false);
						this._alertService.errorByCassError(error);
					}
				}));
	}

	downloadCampaignReportPDF(orderId: string, reportName: string) {
		this._callingOut$.next(true);
		this.subs.add(
			this._orderService.downloadCampaignReportPDF(orderId)
				.subscribe({
					next: (value) => {
						this._orderReportHelperService.saveReportAsFile(value, reportName);
						this._callingOut$.next(false);
					},
					error: (error: CassError) => {
						this._callingOut$.next(false);
						this._alertService.errorByCassError(error);
					}
				})
		);

	}

	change() {
		// Redirect to /myAds with advertiserId & AgencyId as queryParams
		this._router.navigate([AppPath.MyAds], {queryParams: {advertiserId: this.externalPlanInfo?.advertiserId, agencyId: this.externalPlanInfo?.agencyId}})
	}

	getReserveOrSaveButtonLabel(): string {
		// Show "Reserve" iff there are new order lines (since they can be reserved)
		//		-OR- the order can be saved as draft (b/c then there is a separate save as draft button and need a Reserve button to reserve).
		// Show "Save" otherwise.
		return (this.restriction.canSaveAsDraft
				|| this.order.orderAdDetails.some(x => !x.id))
			? "ORDER_BASKET.ACTION_BUTTON_LABELS.RESERVE" : "ACTION_BUTTON_LABELS.SAVE"
	}
}
